import { capitalize } from 'lodash';
import { APP_NAME } from './config';

/**
 * UI Labels and Text Constants
 *
 * Centralized constants for maintaining consistent spelling and verbiage
 * across the application. Organized by functional categories.
 */

// =============================================================================
// COMMON WORDS
// =============================================================================

/**
 * Commonly used single words in the application.
 */
export const WORDS = {
  ACHIEVEMENT: 'achievement',
  ACTIVITY: 'activity',
  ATTEMPT: 'attempt',
  AUDIENCE: 'audience',
  CATEGORY: 'category',
  CERTIFICATE: 'certificate',
  CERTIFICATION: 'certification',
  CERTIFICATIONS: 'certifications',
  CERTIFIED: 'certified',
  COMPLETE: 'complete',
  COMPLETED: 'completed',
  COMPLIANCE: 'compliance',
  CONGRATS: 'congratulations',
  DASHBOARD: 'dashboard',
  DATE: 'date',
  DETAILS: 'details',
  DEVELOPER: 'developer',
  FAIL: 'fail',
  FAILED: 'failed',
  LEVEL: 'level',
  LOADING: 'loading',
  MANAGER: 'manager',
  PASS: 'pass',
  PRACTICE: 'practice',
  PRODUCT: 'product',
  PROFILE: 'profile',
  ROLEPLAY: 'role-play',
  SCENARIO: 'scenario',
  SCENARIOS: 'scenarios',
  SCORE: 'score',
  SCORECARD: 'scorecard',
  SIMULATION: 'simulation',
  STATUS: 'status',
  STEP: 'step',
  UNKNOWN: 'unknown',
  USER: 'user',
} as const;

/**
 * Brand-specific words that may be used in labels and messaging.
 */
export const BRAND_WORDS = {
  APP_SPECIALIST: 'App Specialist',
  CONTRACT_SALES_ORG: 'Contract Sales Organization',
  DISTRICT_MANAGER: 'District Manager',
  FIELDONE_ROLEPLAY_CERT_PLATFORM: 'FieldOne Role-Play & Certification Platform',
  FIELDONE: 'FieldOne',
  FIELDONEc: 'FieldOne©',
  MAINTAINER: 'Maintainer',
  MANAGER: capitalize(WORDS.MANAGER),
  NATIONAL_SALES_DIRECTOR: 'National Sales Director',
  REGIONAL_BUSINESS_DIRECTOR: 'Regional Business Director',
  SALES_REPRESENTATIVE: 'Sales Representative',
  SALES_TRAINING_PLATFORM: 'J&J Sales Training Platform',
} as const;

// =============================================================================
// COMMON TEXT PATTERNS
// =============================================================================

/**
 * Common text patterns used in various contexts.
 */
export const COMMON_TEXT = {
  // Generic Messages
  GOODBYE: 'Goodbye',
  PLEASE_WAIT: 'Please wait',
  THANK_YOU: 'Thank you',
  WELCOME: 'Welcome',

  // Time References
  JUST_NOW: 'Just now',
  NOW: 'Now',
  TODAY: 'Today',
  TOMORROW: 'Tomorrow',
  YESTERDAY: 'Yesterday',

  // Quantities
  ALL: 'All',
  NONE: 'None',
  SELECTED: 'Selected',
  AVAILABLE: 'Available',
  TOTAL: 'Total',

  // States
  ACTIVE: 'Active',
  DISABLED: 'Disabled',
  ENABLED: 'Enabled',
  HIDDEN: 'Hidden',
  INACTIVE: 'Inactive',
  VISIBLE: 'Visible',

  // Consent & Privacy
  CONSENT_ACCEPTED: 'Consent accepted',
  CONSENT_DECLINED: 'Consent declined',
  PRIVACY_POLICY: 'Privacy Policy',
  TERMS_OF_SERVICE: 'Terms of Service',

  // App specific
  CERTIFICATE: capitalize(WORDS.CERTIFICATE),
  DASHBOARD: capitalize(WORDS.DASHBOARD),
  FINAL_CERTIFICATION: `Final ${capitalize(WORDS.CERTIFICATION)}`,
  MANAGER: capitalize(BRAND_WORDS.MANAGER),
  PRACTICE_CERTIFICATION: `${capitalize(WORDS.PRACTICE)} ${capitalize(WORDS.CERTIFICATION)}`,
  PRACTICE_SCORE: `${capitalize(WORDS.PRACTICE)} ${WORDS.SCORE}`,
  PRECALL_PLANNING: 'Pre-call planning',
  PROFILE: capitalize(WORDS.PROFILE),
  ROLEPLAY: capitalize(WORDS.ROLEPLAY),
  SALES_TRAINING_PLATFORM: BRAND_WORDS.SALES_TRAINING_PLATFORM,
  SCORE: capitalize(WORDS.SCORE),
  SCORECARD: capitalize(WORDS.SCORECARD),
  SIMULATION: capitalize(WORDS.SIMULATION),
  USER_PROFILE: `${capitalize(WORDS.USER)} ${WORDS.PROFILE}`,

  // Misc
  PRINT_DOCUMENT: 'Print document',
} as const;

// =============================================================================
// ROLE LABELS
// =============================================================================

/**
 * User role labels used throughout the application.
 */
export const ROLE_LABELS = {
  amb: BRAND_WORDS.APP_SPECIALIST,
  cso: BRAND_WORDS.CONTRACT_SALES_ORG,
  developer: capitalize(WORDS.DEVELOPER),
  dm: BRAND_WORDS.DISTRICT_MANAGER,
  maintainer: BRAND_WORDS.MAINTAINER,
  manager: BRAND_WORDS.MANAGER,
  nsd: BRAND_WORDS.NATIONAL_SALES_DIRECTOR,
  rbd: BRAND_WORDS.REGIONAL_BUSINESS_DIRECTOR,
  rep: BRAND_WORDS.SALES_REPRESENTATIVE,
} as const;

// =============================================================================
// BUTTON LABELS
// =============================================================================

/**
 * Button labels used throughout the application.
 */
export const BUTTON_LABELS = {
  // Common Actions
  CANCEL: 'Cancel',
  CLOSE: 'Close',
  CONFIRM: 'Confirm',
  CONTINUE: 'Continue',
  GET_STARTED: 'Get started',
  OK: 'OK',
  SAVE: 'Save',
  SUBMIT: 'Submit',
  TRY_AGAIN: 'Try again',

  // CRUD Operations
  ADD: 'Add',
  CREATE: 'Create',
  DELETE: 'Delete',
  EDIT: 'Edit',
  ERROR: 'Error',
  REMOVE: 'Remove',
  UPDATE: 'Update',

  // Navigation
  BACK: 'Back',
  DASHBOARD: capitalize(WORDS.DASHBOARD),
  GO_TO_DASHBOARD: `Go to ${WORDS.DASHBOARD}`,
  RETURN_TO_DASHBOARD: `Return to ${WORDS.DASHBOARD}`,
  RETURN_TO_TEAM_DASHBOARD: `Return to team ${WORDS.DASHBOARD}`,
  HOME: 'Home',
  NEXT: 'Next',
  PREVIOUS: 'Previous',
  SIGN_IN: 'Sign in',

  // Share/Manager Actions
  SHARE: 'Share',
  SHARED: 'Shared!',
  SHARE_WITH_MANAGER: `Share with ${WORDS.MANAGER}`,
  SHARED_WITH_MANAGER: `Shared with ${WORDS.MANAGER}`,
  SHARING_WITH_MANAGER: `Sharing with ${WORDS.MANAGER}`,

  // View Actions
  SHOW_LESS: 'Show less',
  SHOW_MORE: 'Show more',
  VIEW_ALL: 'View all',
  VIEW_CERTIFICATE: `View ${WORDS.CERTIFICATE}`,
  VIEW_DETAILS: 'View details',
  VIEW_PROFILE: 'View profile',
  VIEW_SCORECARD: `View ${WORDS.SCORECARD}`,
  VIEW: 'View',

  // Session Management
  END_SESSION: 'End session',
  EXTEND_SESSION: 'Extend session',
  NEXT_ROLEPLAY: `Next ${WORDS.ROLEPLAY}`,
  RETAKE_PRACTICE: `Retake ${WORDS.PRACTICE}`,
  RETAKE_ROLEPLAY: `Retake ${WORDS.ROLEPLAY}`,
  START_CERTIFICATION: `Start ${WORDS.CERTIFICATION}`,
  START_FINAL_CERTIFICATION: `Start ${COMMON_TEXT.FINAL_CERTIFICATION}`,
  START_PRACTICE_CERTIFICATION: `Start ${COMMON_TEXT.PRACTICE_CERTIFICATION}`,
  START_PRACTICE: `Start ${WORDS.PRACTICE}`,
  START_ROLEPLAY: `Start ${WORDS.ROLEPLAY}`,
  START_SESSION: 'Start session',

  // Media Controls
  ENABLE_VIDEO: 'Enable video',
  MUTE: 'Mute',
  SWITCH_TO_AUDIO_ONLY: 'Switch to audio only',
  UNMUTE: 'Unmute',

  // Generic Responses
  MAYBE_LATER: 'Maybe later',
  NO_THANK_YOU: 'No, thank you',
  NO: 'No',
  YES: 'Yes',

  // Loading States
  LOADING: 'Loading...',
  PROCESSING: 'Processing...',
  SAVING: 'Saving...',

  // Misc
  APP_HELP_SUPPORT: 'Help & support',
  APP_SUPPORT_RESOURCES: `${BRAND_WORDS.FIELDONE} support resources`,
  CERTIFICATION_PRACTICE: COMMON_TEXT.PRACTICE_CERTIFICATION,
  FINAL_CERTIFICATION: COMMON_TEXT.FINAL_CERTIFICATION,
  NEXT_SCORECARD: `Next ${WORDS.SCORECARD}`,
  PDF_DOWNLOAD: 'Download PDF',
  PDF_PRINT: 'Print PDF',
  PRINT_CERTIFICATE: `Print ${WORDS.CERTIFICATE}`,
  USER_PROFILE: `${capitalize(WORDS.USER)} ${WORDS.PROFILE}`,
} as const;

// =============================================================================
// ARIA LABELS & ACCESSIBILITY
// =============================================================================

/**
 * ARIA labels for accessibility purposes.
 */
export const ARIA_LABELS = {
  // Navigation
  EXPAND_NAVIGATION: 'Expand navigation',
  COLLAPSE_NAVIGATION: 'Collapse navigation',
  MAIN_NAVIGATION: 'Main navigation',

  // Notifications
  CLOSE_NOTIFICATION: 'Close notification',

  // Loading States
  LOADING_CONTENT: 'Loading...',
  LOADING_PROGRESS: 'Score processing progress',

  // Buttons
  SHARE_WITH_MANAGER: `Share this ${WORDS.SCORECARD} with your ${WORDS.MANAGER}`,
  SHARED_WITH_MANAGER: `This ${WORDS.SCORECARD} has already been shared with your ${WORDS.MANAGER}`,
  SHARING_WITH_MANAGER: `Sharing this ${WORDS.SCORECARD} with your ${WORDS.MANAGER}`,

  // Modal Actions
  CLOSE_MODAL: 'Close modal',
  CLOSE_DIALOG: 'Close dialog',

  // View actions
  SHOW_FEWER_ENTRIES: 'Show fewer entries',
  SHOW_LESS_ENTRIES: 'Show less entries',
  SHOW_MORE_ENTRIES: 'Show more entries',

  // CRUD Actions
  ERROR: 'An error occurred. Please try again.',

  // Scorecards
  SCORE_DISPLAY: 'Score display',
  SCORE_OUT_OF: `Score: %SCORE_VALUE% of %MAX_SCORE%`,
  SCORE_PROCESSING_PROGRESS: `Score processing progress at %PROGRESS_VALUE%%`,
} as const;

// =============================================================================
// STATUS MESSAGES
// =============================================================================

/**
 * Status messages displayed to users.
 */
export const STATUS_MESSAGES = {
  // Success Messages
  SUCCESS: 'Success!',
  SAVED_SUCCESSFULLY: 'Your data has been successfully saved.',
  SESSION_EXTENDED: 'Session has been extended successfully.',

  // Warning Messages
  SESSION_EXPIRING: 'Your session will expire in 5 minutes. Please save your work.',
  UNSTABLE_CONNECTION: 'Internet is unstable, do you want to switch to audio only?',
  UNSAVED_CHANGES: 'You have unsaved changes. Are you sure you want to leave?',
  COMPLIANCE_REMINDERS: `You have Compliance Reminders. No worries — this is a safe place to learn. Please review the full scorecard to spot strengths and areas to grow as you master your craft.`,

  // Error Messages
  GENERIC_ERROR: 'An error occurred. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SHARE_ERROR: 'Error sharing with manager',
  UNKNOWN_ERROR: 'Unknown error occurred',

  // Loading Messages
  LOADING_DATA: `${capitalize(WORDS.LOADING)} data...`,
  LOADING: `${capitalize(WORDS.LOADING)}...`,
  PROCESSING_SCORE_DESCRIPTION: `Generating your ${WORDS.SCORECARD} now — this usually takes just a few seconds. Hang tight, we're almost there.`,
  PROCESSING_SCORE: `Processing your ${WORDS.SCORE}...`,
  REDIRECT_DASHBOARD: `Redirecting to your ${WORDS.DASHBOARD}...`,
  SAVING_CHANGES: 'Saving changes...',

  // Info Messages
  NO_DATA: 'No data available.',
  NO_NOTIFICATIONS: 'No active notifications. Use the buttons above to add some!',
  OUTAGE_TITLE: `Scheduled maintenance`,
  OUTAGE_DESCRIPTION: `We are performing a scheduled maintenance on ${APP_NAME} to apply updates, improve performance, and enhance stability. During this time, all users will be impacted, and ${APP_NAME} will not be available. You will not be able to login or access your scenarios. We will notify you once the system is fully restored and available for normal use.`,

  // Login & Authentication
  COMPLETING_SIGN_IN: 'Completing Sign In...',
  PLEASE_WAIT_AUTH: 'Please wait while we complete your authentication',
} as const;

// =============================================================================
// CARDS
// =============================================================================

/**
 * Card labels and related constants.
 */
export const CARD_LABELS = {
  CERTIFICATION_SCORE: `${capitalize(WORDS.CERTIFICATION)} ${WORDS.SCORE}`,
  COMPLIANCE_REMINDERS: `${capitalize(WORDS.COMPLIANCE)} reminders`,
  NEXT_ROLEPLAY: `Next ${WORDS.ROLEPLAY}`,
  PRACTICE_CERTIFICATION_SCORE: `${capitalize(`${WORDS.PRACTICE}`)} ${capitalize(WORDS.CERTIFICATION)} ${WORDS.SCORE}`,
  PREVIOUS_SCORES: `Previous ${WORDS.SCORE}s`,
  ROLEPLAY_PRACTICE_SCENARIOS: `${capitalize(WORDS.ROLEPLAY)} ${WORDS.PRACTICE} ${WORDS.SCENARIOS}`,
  ROLEPLAY_SCORE: `${capitalize(WORDS.ROLEPLAY)} ${WORDS.SCORE}`,
  SCORE_PROCESSING_DESCRIPTION:
    'This may take up to 30 minutes to process. Please visit your dashboard to view results when ready.',
  SCORE_PROCESSING_TITLE: 'Your score is processing.',
  USER_PROFILE: BUTTON_LABELS.USER_PROFILE,
} as const;

// =============================================================================
// TABLES
// =============================================================================

/**
 * Table column headers and related labels.
 */
export const TABLE_LABELS = {
  // Common Columns
  ACTIONS: 'Actions',
  ACTIVITY: capitalize(WORDS.ACTIVITY),
  DATE: `${capitalize(WORDS.DATE)}`,
  DELETE: 'Delete',
  EDIT: 'Edit',
  EMPTY: '—',
  NA: 'N/A',
  NAME: 'Name',
  NO_RECORDS: 'No records found.',
  PRODUCT: capitalize(WORDS.PRODUCT),
  SCORE: capitalize(WORDS.SCORE),
  STATUS: capitalize(WORDS.STATUS),
  VIEW: 'View',

  // Specific Tables
  CERTIFICATION_FINAL: `Final ${WORDS.CERTIFICATION}`,
  CERTIFICATION_STATUS: `${capitalize(WORDS.CERTIFICATION)} status`,
  DATE_CERTIFIED: `${capitalize(WORDS.DATE)} ${WORDS.CERTIFIED}`,
  DATE_COMPLETED: `${capitalize(WORDS.DATE)} ${WORDS.COMPLETED}`,
  LAST_ACTIVITY: `Last ${WORDS.ACTIVITY}`,
  NO_ACTIVITY: `No ${WORDS.ACTIVITY}`,
  NO_ATTEMPTS: 'No attempts',
  NONE_SHARED: 'None shared',
  NONE_TAKEN: 'None taken',
  PRACTICE_CERTIFICATION: `${capitalize(WORDS.PRACTICE)} ${capitalize(WORDS.CERTIFICATION)}`,
  ROLEPLAY_PRACTICE: `${capitalize(WORDS.ROLEPLAY)} ${WORDS.PRACTICE}`,
  ROLEPLAY: capitalize(WORDS.ROLEPLAY),
  SALES_REPRESENTATIVE: BRAND_WORDS.SALES_REPRESENTATIVE,
  SHARED_ROLEPLAY: `Shared ${WORDS.ROLEPLAY}`,
  SHOWING_FILTER_RESULTS: `Showing filtered results for`,
  TEAM_MEMBERS: 'Team members',
  UNAVAILABLE: 'Unavailable',
} as const;

// =============================================================================
// TAGS & BADGES
// =============================================================================

/**
 * Labels for tags and badges used in the application.
 */
export const TAG_LABELS = {
  ACTIVE: 'Active',
  CERTIFIED: capitalize(WORDS.CERTIFIED),
  COMPLIANCE_REMINDER: `${capitalize(WORDS.COMPLIANCE)} reminders`,
  DEVELOPING: 'Developing',
  FAIL_1ST_ATTEMPT: 'Failed 1st attempt',
  FAIL_2ND_ATTEMPT: 'Failed 2nd attempt',
  FAIL_2PLUS_ATTEMPTS: 'Failed 2+ attempts',
  FAIL_CERTIFICATION: `Failed ${WORDS.CERTIFICATION}`,
  FAIL_PRACTICE: `Failed ${WORDS.PRACTICE}`,
  FAIL: capitalize(WORDS.FAIL),
  INACTIVE: 'Inactive',
  NO_ATTEMPTS: 'No attempts',
  PASS: capitalize(WORDS.PASS),
  READY_TO_CERTIFY: 'Ready to certify',
} as const;

// =============================================================================
// FORM LABELS & PLACEHOLDERS
// =============================================================================

/**
 * Form field labels and placeholders.
 */
export const FORM_LABELS = {
  // Common Fields
  CONFIRM_PASSWORD: 'Confirm Password',
  DESCRIPTION: 'Description',
  EMAIL: 'Email',
  MESSAGE: 'Message',
  NAME: 'Name',
  PASSWORD: 'Password',
  PHONE: 'Phone',
  SUBJECT: 'Subject',
  TITLE: 'Title',

  // Placeholders
  ENTER_EMAIL: 'Enter your email',
  ENTER_MESSAGE: 'Enter your message',
  ENTER_NAME: 'Enter your name',
  SEARCH_PLACEHOLDER: 'Search...',
  SELECT_CERTIFICATION: `Select ${WORDS.CERTIFICATION}`,

  // Manager
  PRODUCT_ALL: 'All products',
  PRODUCT_CHANGE: 'Change product',
  PRODUCT_SELECT: 'Select product',
} as const;

// =============================================================================
// SIMULATION LABELS
// =============================================================================

/**
 * Labels related to simulations, role-plays, practices, and certifications.
 */
export const SIMULATION_LABELS = {
  CERTIFICATE: capitalize(WORDS.CERTIFICATE),
  CERTIFICATION_FINAL: `Final ${WORDS.CERTIFICATION}`,
  CERTIFICATION_SCENARIOS: `${capitalize(WORDS.CERTIFICATION)} ${WORDS.SCENARIOS}`,
  CERTIFICATION_SCORES: `Final ${WORDS.CERTIFICATION} ${WORDS.SCORE}s`,
  CERTIFICATION: capitalize(WORDS.CERTIFICATION),
  CERTIFICATIONS_COMPLETED: `${capitalize(WORDS.CERTIFICATION)}s completed`,
  CERTIFICATIONS_NO_BADGES: `Complete your first ${WORDS.CERTIFICATION}!`,
  CERTIFIED: capitalize(WORDS.CERTIFIED),
  FAIL: TAG_LABELS.FAIL,
  FAILED_CERTIFICATION: `Failed ${WORDS.CERTIFICATION}`,
  FAILED_PRACTICE: `Failed ${WORDS.PRACTICE}`,
  PASS: TAG_LABELS.PASS,
  PRACTICE_CERTIFICATION: `${capitalize(WORDS.PRACTICE)} ${capitalize(WORDS.CERTIFICATION)}`,
  PRACTICE_SCORES: `${capitalize(WORDS.PRACTICE)} ${capitalize(WORDS.CERTIFICATION)} ${WORDS.SCORE}s`,
  PRACTICE: capitalize(WORDS.PRACTICE),
  PREVIOUS_CERTIFICATION_SCORES: `Final ${WORDS.CERTIFICATION} ${WORDS.SCORE}s`,
  PREVIOUS_PRACTICE_SCORES: `Previous ${WORDS.PRACTICE} ${WORDS.SCORE}s`,
  PREVIOUS_ROLEPLAY_SCORES: `Previous ${WORDS.ROLEPLAY} ${WORDS.SCORE}s`,
  ROLEPLAY_PRACTICE_SCENARIO: `${capitalize(WORDS.ROLEPLAY)} ${WORDS.PRACTICE} ${WORDS.SCENARIO}`,
  ROLEPLAY_PRACTICE_SCENARIOS: `${capitalize(WORDS.ROLEPLAY)} ${WORDS.PRACTICE} ${WORDS.SCENARIOS}`,
  ROLEPLAY_PRACTICE: `${capitalize(WORDS.ROLEPLAY)} ${WORDS.PRACTICE}`,
  ROLEPLAY: `${capitalize(WORDS.ROLEPLAY)}`,
  SHARED_ROLEPLAY: `Shared ${WORDS.ROLEPLAY}`,
  STEP: capitalize(WORDS.STEP),
} as const;

// =============================================================================
// MANAGER LABELS
// =============================================================================

/**
 * Labels specific to manager views and actions, particularly around certifications
 * and progress tracking.
 */
export const MANAGER_LABELS = {
  CERTIFICATION_SCORECARDS_FINAL: `${COMMON_TEXT.FINAL_CERTIFICATION} ${WORDS.SCORECARD}s`,
  CERTIFICATION_SCORECARDS: `${capitalize(WORDS.CERTIFICATION)} ${WORDS.SCORECARD}s`,
  FILTER_CERTIFIED: `Team members ${WORDS.CERTIFIED}`,
  FILTER_NEEDS_ATTENTION: 'Needs attention',
  FILTER_PRACTICE: `Passed ${SIMULATION_LABELS.PRACTICE_CERTIFICATION}`,
  MANAGED_USER_DESCRIPTION_CERTIFICATIONS: `Please monitor your team's progress regularly. Practice certification is required before the Final Certification is unlocked — representatives should pass the practice certification before attempting the Final. You (as the manager) will receive scorecards for every practice attempt and every Final Certification attempt. This visibility is intentional so you can track progress and provide targeted coaching where needed. Keep in mind that reps are allowed 2 attempts to certify. The below guidance is being provided so you can mitigate any failed Final Certification attempts.\n\n\- Upon a first FAILED attempt reps are encouraged to wait 24 hours prior to attempting another certification. During this time we recommend you connect with your rep and provide any coaching needed based on the scorecard results\n\- Upon a second FAILED attempt the user will be locked from certifications and a human-to human certification is required`,
  MANAGED_USER_DESCRIPTION_PRACTICES: `Much like Final Certification Scorecards, all Practice Certification Scorecards are shared with you automatically. We have done this so you can track rep's progress towards final certification more closely and identify any targeted coaching needs. It is important to note that all reps must complete at least one Practice Certification prior to attempting a Final Certification. This is meant to act as a "Rehearsal" for the representative and build confidence in their ability to pass a certification. All Practice Certification Scorecards and their status and date completed are shown below.`,
  MANAGED_USER_DESCRIPTION_ROLEPLAYS: `FieldOne is designed to be a safe space for reps to practice and build skills. Sharing Role-Play practice scorecards is voluntary. Reps decide which practice scorecards to share with you. Treat the shared scorecards as opportunities for constructive, confidential coaching. If you need broader insight into a rep's progress, request that they share additional scorecards. Explain how increased visibility will help you provide focused feedback and faster improvement. Below are the scorecards that your reps have decided to share.`,
  PRACTICE_CERTIFICATION_SCORECARDS: `${COMMON_TEXT.PRACTICE_CERTIFICATION} ${WORDS.SCORECARD}s`,
  SHARED_ROLEPLAY_SCORECARDS: `${SIMULATION_LABELS.SHARED_ROLEPLAY} ${WORDS.SCORECARD}s`,
  TEAM_DASHBOARD_DESCRIPTION: `Monitor role-plays & certifications, celebrate progress, and deliver targeted coaching — a safe space to review scorecards and grow your team.`,
  TEAM_DASHBOARD_TITLE: `Team readiness ${WORDS.DASHBOARD}`,
  UNLOCK_DENIAL_ALREADY_CERTIFIED: `This representative already passed certification and cannot be unlocked.`,
  UNLOCK_DENIAL_API_FAILED: `Unable to process unlock right now. Please try again.`,
  UNLOCK_DENIAL_MISSING_CONTEXT: `Missing unlock context. Refresh the page and try again.`,
  UNLOCK_DENIAL_NOT_DIRECT_REPORT: `You can only unlock certifications for your direct reports.`,
  UNLOCK_DENIAL_NOT_LOCKED: `This certification is not currently locked for this representative.`,
  UNLOCK_DENIAL_SCENARIO_MISMATCH: `Selected certification context does not match this unlock request.`,
  UNLOCK_MODAL_TITLE: `Confirm unlock`,
  UNLOCK_MODAL_DESCRIPTION: `This will re-enable certification attempts for the representative. Please ensure you have provided targeted coaching and a live certification (if necessary) prior to clicking "${BUTTON_LABELS.CONFIRM}".`,
} as const;

// =============================================================================
// SKILL LEVELS & SCORES
// =============================================================================

/**
 * Skill levels used in the application.
 */
export const SKILL_LEVELS = {
  ADVANCED: 'advanced',
  AWARENESS: 'awareness',
  BEGINNER: 'beginner',
  EXPERT: 'expert',
  INTERMEDIATE: 'intermediate',
  NOVICE: 'novice',
  SKILLED: 'skilled',
} as const;

/**
 * Labels for skill levels.
 */
export const SKILL_LEVEL_LABELS = {
  L1: capitalize(SKILL_LEVELS.AWARENESS),
  L2: capitalize(SKILL_LEVELS.SKILLED),
  L3: capitalize(SKILL_LEVELS.ADVANCED),
  L4: capitalize(SKILL_LEVELS.EXPERT),
} as const;

/**
 * Labels for scores and scorecards, particularly in the context of
 * certifications and simulations.
 */
export const SCORE_LABELS = {
  // ===============================================
  // General score labels
  // ===============================================
  FAIL: TAG_LABELS.FAIL,
  NO_PREVIOUS_SCORES_AVAILABLE: `No previous ${WORDS.SCORE}s available`,
  NO_PREVIOUS_SCORES: `No previous ${WORDS.SCORE}s`,
  PASS: TAG_LABELS.PASS,
  READY_TO_CERTIFY: TAG_LABELS.READY_TO_CERTIFY,
  PREVIOUS_SCORES: CARD_LABELS.PREVIOUS_SCORES,

  // ===============================================
  // Skill level descriptions
  // ===============================================

  /**
   * Scorecard description for skill level 1 - novice
   * @deprecated This label is no longer used in the application
   */
  USER_ROLEPLAY_LEVEL_1_NOVICE: `You're building a strong foundation! Review the feedback and implement it in your next practice sessions; every expert started here.`,

  /**
   * Scorecard description for skill level 2 - awareness
   */
  USER_ROLEPLAY_LEVEL_2_BEGINNER: `You're gaining momentum! Review and apply feedback to master your skills and level-up.`,

  /**
   * Scorecard description for skill level 3 - intermediate
   */
  USER_ROLEPLAY_LEVEL_3_INTERMEDIATE: `Strong progress! Review your feedback, celebrate your wins, apply key learnings, and keep practicing to move confidently toward Advanced status.`,

  /**
   * Scorecard description for skill level 4 - advanced
   */
  USER_ROLEPLAY_LEVEL_4_ADVANCED: `Review your feedback, leverage your wins, and fine-tune your skills in the next practice to reach Expert.`,

  /**
   * Scorecard description for skill level 5 - expert
   */
  USER_ROLEPLAY_LEVEL_5_EXPERT: `Only perfect practice makes perfect! Celebrate your mastery, share your success with your District Manager, and keep innovating to stay on top.`,

  // ===============================================
  // Score messages
  // ===============================================

  /**
   * `[FAIL]` Used for scorecard description
   */
  USER_CERTIFICATION_FAIL: `Unfortunately you did not pass your ${WORDS.CERTIFICATION}. Connect with your DM to discuss some areas of improvement and use the ${BUTTON_LABELS.CERTIFICATION_PRACTICE} module to apply your learnings.`,

  /**
   * `[FAIL]` Used for the page's subtitle when user fails a certification.
   *
   * Note that `\b` in the string is used to create a line break for better readability,
   * especially given the length of the message.
   *
   * Also note that `_1` signifies the user's first attempt at the certification. If the user
   * fails again, the `_2` message will be used, which provides additional encouragement and
   * guidance for next steps.
   */
  USER_CERTIFICATION_FAIL_SUBTITLE_1: `We know that certification can be challenging, and while you didn't pass this attempt, you're not alone on your journey. We recommend waiting at least 24 hours before making your second attempt. During this time:\n\n\- Review the feedback below with your District Manager (DM) to pinpoint areas for improvement\n\- Use the Practice Certification module to incorporate coaching before your next try\n\nYou have one more attempt available—and if you need additional support, a live certification with your DM will be required after that.`,

  /**
   * `[FAIL]` Used for the page's subtitle when user fails a second time.
   */
  USER_CERTIFICATION_FAIL_SUBTITLE_2: `This second attempt to certify was unsuccessful and you will need to proceed to a live in-person certification. Reach out to your DM to schedule your in-person certification.`,

  /**
   * `[PASS]` Used for scorecard description
   */
  USER_CERTIFICATION_PASS: `Congratulations! You passed your ${WORDS.CERTIFICATION}. Click on the link below to view your ${WORDS.CERTIFICATE}.`,

  /**
   * `[PASS]` Used for the page's subtitle when user passes a certification,
   * providing more context on what it means to be certified.
   *
   * Note that `%CERTIFICATION_TITLE%` is a placeholder that should be replaced with the actual title of the certification the user passed, to provide a more personalized message. For example, if the certification title is "Sales Mastery", the message would read:
   *
   * `"Congratulations! You have successfully certified in the FieldOne© Platform to deliver Sales Mastery messaging in accordance with brand and compliance guidelines."`
   */
  USER_CERTIFICATION_PASS_SUBTITLE: `${capitalize(WORDS.CONGRATS)}! You have successfully certified in the ${BRAND_WORDS.FIELDONE} platform to deliver %CERTIFICATION_TITLE% messaging in accordance with brand and compliance guidelines.`,

  /**
   * `[FAIL]` Used for scorecard description when user fails a practice certification.
   */
  USER_PRACTICE_FAIL: `Hey... this is why we practice! Work with your District Manager to identify areas for development and give it another practice round before you move to the ${COMMON_TEXT.FINAL_CERTIFICATION}.`,

  /**
   * `[FAIL]` Used for the page's subtitle when user fails a practice certification,
   * providing encouragement and next steps.
   */
  USER_PRACTICE_FAIL_SUBTITLE: `You gave it a solid effort — not passing is part of learning and this was only a ${WORDS.PRACTICE} run. We let your District Manager know so you can review your feedback together. Pull through your wins, refine your areas for development and try another ${WORDS.PRACTICE} when you feel ready.`,

  /**
   * `[PASS]` Used for scorecard description when user passes a practice certification.
   */
  USER_PRACTICE_PASS: `Congratulations! You passed your ${capitalize(WORDS.PRACTICE)} ${capitalize(WORDS.CERTIFICATION)}. Connect with your DM to discuss your progress and use the ${BUTTON_LABELS.CERTIFICATION_PRACTICE} module to continue improving.`,

  /**
   * `[PASS]` Used for the page's subtitle when user passes a practice certification,
   * providing encouragement and next steps.
   */
  USER_PRACTICE_PASS_SUBTITLE: `Great work — you've passed the ${capitalize(WORDS.PRACTICE)} ${capitalize(WORDS.CERTIFICATION)}! Celebrate this progress with your District Manager. Review your feedback, feel free to run another focused practice to sharpen key development areas; otherwise, move on to the Final ${capitalize(WORDS.CERTIFICATION)} when you're ready.`,
} as const;

// =============================================================================
// CERTIFICATE LABELS
// =============================================================================

/**
 * Labels related to certifications, including certificate details and page subtitles.
 *
 * Note that some of these labels include placeholders (e.g. `%CERTIFICATION_TITLE%`) that
 * should be dynamically replaced with specific information at runtime to provide a more
 * personalized experience for the user.
 */
export const CERTIFICATE_LABELS = {
  CERTIFICATE_COMPLETION_DATE: 'Completion date',
  CERTIFICATE_ISSUED_BY: 'Issued by',
  CERTIFICATE_ISSUED_TO: 'Issued to',
  CERTIFICATE_TITLE: `${capitalize(WORDS.CERTIFICATE)} of ${capitalize(WORDS.ACHIEVEMENT)}`,
  CERTIFICATE_USER_PREFIX: 'This acknowledges that',
  CERTIFICATE_USER_SUFFIX: `has successfully completed the following ${WORDS.CERTIFICATION}:`,
  CERTIFICATE: capitalize(WORDS.CERTIFICATE),
  CERTIFICATION: capitalize(WORDS.CERTIFICATION),
  PAGE_SUBTITLE: `You have successfully ${WORDS.CERTIFIED} in the ${BRAND_WORDS.FIELDONE} platform to deliver %CERTIFICATION_TITLE% messaging in accordance with brand and compliance guidelines. Be sure to download and save or print your certification for your records.`,
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type AriaLabel = (typeof ARIA_LABELS)[keyof typeof ARIA_LABELS];
export type BrandWord = (typeof BRAND_WORDS)[keyof typeof BRAND_WORDS];
export type ButtonLabel = (typeof BUTTON_LABELS)[keyof typeof BUTTON_LABELS];
export type CardLabel = (typeof CARD_LABELS)[keyof typeof CARD_LABELS];
export type CertificateLabel = (typeof CERTIFICATE_LABELS)[keyof typeof CERTIFICATE_LABELS];
export type CommonText = (typeof COMMON_TEXT)[keyof typeof COMMON_TEXT];
export type FormLabel = (typeof FORM_LABELS)[keyof typeof FORM_LABELS];
export type RoleLabel = (typeof ROLE_LABELS)[keyof typeof ROLE_LABELS];
export type ManagerLabel = (typeof MANAGER_LABELS)[keyof typeof MANAGER_LABELS];
export type ScoreLabel = (typeof SCORE_LABELS)[keyof typeof SCORE_LABELS];
export type SimulationLabel = (typeof SIMULATION_LABELS)[keyof typeof SIMULATION_LABELS];
export type SkillLevel = (typeof SKILL_LEVELS)[keyof typeof SKILL_LEVELS];
export type StatusMessage = (typeof STATUS_MESSAGES)[keyof typeof STATUS_MESSAGES];
export type TableLabel = (typeof TABLE_LABELS)[keyof typeof TABLE_LABELS];
export type TagLabel = (typeof TAG_LABELS)[keyof typeof TAG_LABELS];
export type Word = (typeof WORDS)[keyof typeof WORDS];
